/**
 * @module app.shared
 */

import { escapeHtml } from 'app/shared/lib/formatting/html-utilities';
import { IModel } from "app/shared/models/essette-interfaces";

export function sleep(milliseconds: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

/**
 * Safely catches TypeErrors when accessing properties on null/undefined instances.
 * Useful for chaining long accessors.  Similar to the C# safe member access (?.) operator.
 *
 * Note: The accessor should use the non-null assertion operator (!.) to avoid TypeScript errors.
 * Example:
 *      const fullName: string = safe(() => targetCase!.member!.name!.full) || 'No Name';
 *
 * @param accessor a lambda function that wraps the value you are trying to access
 */
export function safe<T>(accessor: () => T): T | undefined {
    try {
        return accessor();
    }
    catch(error) {
        if(error instanceof TypeError) {
            return undefined;
        }
        throw error;
    }
}

export const displayComparer = (a: IModel | null | undefined, b: IModel | null | undefined): number => {
    if(a && a.$display && b && b.$display) {
        if(a.$display < b.$display) return -1;
        if(a.$display > b.$display) return 1;
    }
    return 0;
}

export type NullOrPartial<T> = null | Partial<T>;
/**
 * Performs deep merge of two objects. Merges sources into target
 * from: https://stackoverflow.com/a/46973278
 * @param target
 * @param sources
 * example usage: mergeObjects(trgObject, srcObject1, srcObject2))
 */
export const mergeObjects = <T extends {}>(target: T, ...sources: NullOrPartial<T>[]): T => {
    if(!sources.length) {
        return target;
    }
    const source: any = sources.shift();
    if(source === undefined || source === null) {
        return target;
    }
    const targetAny: any = target;

    if(canMerge(target) && canMerge(source)) {
        Object.keys(source as any).forEach(
            (key: string) => {
                if(canMerge(source[key])) {
                    if(!targetAny[key]) {
                        targetAny[key] = {};
                    }
                    mergeObjects(targetAny[key], source[key]);
                }
                else {
                    targetAny[key] = source[key];
                }
            });
    }

    return mergeObjects(target, ...sources);
};
const isObject = (item: any): boolean => {
    return item !== null && typeof item === 'object';
};
const canMerge = (item: any): boolean => {
    return isObject(item) && !Array.isArray(item);
};

export function deepClone<T extends {}>(source: T): T;
export function deepClone<T extends {}>(source: T | null | undefined): T | null | undefined {
    if(isNullOrUndefined(source)) {
        return source;
    }
    return JSON.parse(JSON.stringify(source)) as T;
}

export function buildJsonPropertyPath(path: string[]): string {
    const results: string[] = [];
    for(const part of path) {
        const integer = parseInt(part, 10);
        if(isNaN(integer)) {
            if(/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(part)) {
                results.push('.', part);
            }
            else {
                results.push('["', part, '"]'); // TODO: escape \, ", and control characters
            }
        }
        else {
            results.push('[', part, ']');
        }
    }
    if(results[0] === '.') {
        results.shift();
    }
    return results.join('');
}

export function encodeQueryString(value: string | null): string {
    return encodeURIComponent(convertToString(value)).replace(/%20/g, '+');
}

export function areStringsEqual(a: string | null, b: string | null, ignoreCase: boolean = false, useLocale: boolean = false): boolean {
    if(a === undefined || a === null) {
        return b === undefined || b === null;
    }
    else if(b === undefined || b === null) {
        return false;
    }
    else if(ignoreCase) {
        if(useLocale) {
            a = a.toLocaleUpperCase();
            b = b.toLocaleUpperCase();
        }
        else {
            a = a.toUpperCase();
            b = b.toUpperCase();
        }
    }
    return a === b;
}

export function convertStringToProperCase(value: string): string {
    const string = value.split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    return `${string}`;
}

export function convertToString(value: unknown, nulls?: false): string;
export function convertToString(value: unknown, nulls?: true): string | null;
export function convertToString(value: unknown, nulls: boolean = false): string | null {
    if(value === undefined || value === null) { return nulls ? null : ''; }
    if(typeof value === 'string') { return value; }
    return `${value}`;
}

export function convertToStringArray(valueOrValues: unknown, keepMissingValues: boolean = false): string[] {
    if(valueOrValues === undefined || valueOrValues === null) { return []; }

    let array: any[] = (Array.isArray(valueOrValues)) ? valueOrValues : [valueOrValues];
    if(!keepMissingValues) {
        array = array.filter(item => !!item);
    }
    return array.map(item => convertToString(item));
}

export function getMinMax(...items: string[]): [string, string] {
    if(items.length == 0) { return ['', '']; }
    if(items.length < 2) { return [items[0], items[0]]; }
    const sorted = items.sort();
    return [sorted[0], sorted[sorted.length - 1]];
}

export function appendOrdinalSuffixDisplay(value?: number | null): string {
    if(isNullOrUndefined(value)) return '';
    if(value <= 0) return value.toString();

    const a = value % 10;
    const b = value % 100;
    let suffix = '';

    if(a === 1 && b !== 11) {
        suffix = 'st';
    } else if(a === 2 && b !== 12) {
        suffix = 'nd';
    } else if(a === 3 && b !== 13) {
        suffix = 'rd';
    } else {
        suffix = 'th';
    }

    return value.toString() + suffix;
}

/**
 * Given an array of `choices` of a limited type, return the one that is equal to `value`,
 * otherwise the first element of `choices`.
 *
 * (The function returns `any` to get around generic resolution rules by return type.  Future
 * TypeScript versions may have a fix for this and the type can be changed to `T`.)
 *
 * **Example**:
 * ```ts
 * interface ICompass {
 *     direction: 'north' | 'south' | 'east' | 'west'
 * }
 *
 * function createCompass(direction: string): ICompass {
 *     return {
 *         direction: oneOf(direction, ['north', 'south', 'east', 'west']);
 *     };
 * }
 * ```
 *
 * @param value the value to check
 * @param choices a list of choices to check against, must have at least one element
 */
export function oneOf<T>(value: any, choices: T[]): T | any {
    if(choices.length === 0) { throw new TypeError('Argument "choices" must have at least one element.') };
    return choices.find(item => item === value) || choices[0];
}

export function range(n: number): Array<number> {
    return Array(n).fill(true).map((_, i) => i);
}

export function pyramid(n: number): Array<number> {
    return range(n).concat(range(n - 1).reverse());
}

export function isNullOrUndefined(v: any): v is null | undefined {
    return v === null || v === undefined;
}

export function forceNull(a: string | null | undefined): string | null {
    if(a === undefined || a?.trim() === '') return null;
    return a;
}

/**
 * Performs a string split, with a string seperator and a limit, but retains the remainder of the string in the last element
 * INFO: [MW] Consider handling RegEx, if needed.
 * @param value the string to split
 * @param separator A string that identifies characters to use in seperating the string
 * @param limit A value used to limit the the number of elements to return in the array
 * @example
 *  const stringToSplit = "param:'To: ~'";
 *  const parts = stringSplitRetain(stringToSplit, ':', 2);
 *  // parts: ["param","'To: ~'"]
  */
export function stringSplitRetain(value: string, separator: string, limit: number) {
    // If the limit is less than 1 then use normal split behavior
    if(limit < 1) {
        return value.split(separator, limit);
    }
    else {
        limit--;  //Adjust limit value for how slice() works
        const parts = value.split(separator);

        return [...parts.slice(0, limit), parts.slice(limit).join(separator)];
    }
}

/**
 * Create a splice of the provided array without modifying the original array.
 * @param array The array to clone and splice.
 * @param start The zero-based location in the array from which to start removing elements.
 * @param deleteCount The number of elements to remove.
 * @returns A copy of the provided array after the splice is applied.
 */
export function spliceCopy<T>(array: T[], start: number, deleteCount?: number): T[] {
    const arrayClone = deepClone(array);
    arrayClone.splice(start, deleteCount);
    return arrayClone;
}

/**
 * Push item(s) to the provided array without modifying the original array.
 * @param array The array to clone and add to.
 * @param items New elements to add to the array.
 * @returns A copy of the provided array after the items are added.
 */
export function pushCopy<T>(array: T[], ...items: T[]): T[] {
    const arrayClone = deepClone(array);
    arrayClone.push(...items);
    return arrayClone;
}

/**
 * Get the appropriate document icon to display based on the media type code
 *
 * @param mediaTypeCode the code passed from a document model's mediaType to determine the document type icon
 */
export function getDocumentIcon(mediaTypeCode: string): string | null {
    if(!mediaTypeCode) { return 'ui:icons.files.$'; }
    if(mediaTypeCode === 'document.html' || mediaTypeCode === 'document.xhtml') { return 'ui:icons.files.document.html'; }
    if(mediaTypeCode === 'document.pdf' || mediaTypeCode === 'document.xhtml') { return 'ui:icons.files.document.pdf'; }
    if(mediaTypeCode === 'generic.text') { return 'ui:icons.files.generic.text'; }
    if(mediaTypeCode.startsWith('document.microsoft.') && mediaTypeCode !== 'document.microsoft.rtf') { return 'ui:icons.files.document.word'; }
    if(mediaTypeCode.startsWith('spreadsheet.microsoft.')) { return 'ui:icons.files.spreadsheet.excel'; }
    if(mediaTypeCode.startsWith('presentation.microsoft.')) { return 'ui:icons.files.presentation.powerpoint'; }
    if(mediaTypeCode === 'data.csv') { return 'ui:icons.files.data.csv'; }
    if(mediaTypeCode === 'data.xml') { return 'ui:icons.files.data.xml'; }
    if(mediaTypeCode === 'data.ical') { return 'ui:icons.files.data.ical'; }
    if(mediaTypeCode === 'data.hl7' || mediaTypeCode.startsWith('data.hl7.')) { return 'ui:icons.files.data.hl7'; }
    const parts = mediaTypeCode.split('.', 2);
    return `ui:icons.files.${parts[0]}.$`;
}

export interface IMarkdownLinkObject {
    hasError: boolean;
    text: string;
    title: string;
    url: string;
}

/**
 * Parses simple markdown language into a markdown link object, which can then be converted to an HTML anchor tag
 *
 * @param value the value to convert
 */
export function parseMarkdownLink(value: unknown): IMarkdownLinkObject {
    const markdownLinkObject: IMarkdownLinkObject = {
        text: '', title: '', url: '', hasError: true
    };

    if(typeof value === 'string') {
        const initialMatch = value.match(/\[([^\]]+)\]\(([^)]+)\)/);

        if(!initialMatch) {
            markdownLinkObject.text = escapeHtml(value)?.html || '';
        } else {
            const [_, bracketText, parenText] = initialMatch;
            const followupMatch = parenText.match(/^(\S+)\s+"([^"]+)"$|^(\S+)"[^"]+"$/);
            if(followupMatch && followupMatch[1] && followupMatch[2]) {
                const [_, url, title] = followupMatch;
                markdownLinkObject.url = encodeURI(url) || '';
                markdownLinkObject.title = escapeHtml(title)?.html || '';
            } else {
                markdownLinkObject.url = encodeURI(parenText) || '';
            }
            markdownLinkObject.text = escapeHtml(bracketText)?.html || '';
            markdownLinkObject.hasError = false;
        }
    }

    return markdownLinkObject;
}

/**
 * Converts a URL to an absolute URL if it is not already absolute.  Relative URLs are resolved relative to the current origin.
 *
 * @param url the URL to convert to an absolute URL
 */
export function getAbsoluteUrl(url: string): string {
    if(!url) { return url; }
    if(/^https?:\/\//i.test(url)) { return url; }
    if(url.startsWith('//')) { return `${window.location.protocol}${url}`; }

    return new URL(url, window.location.origin).toString();
}
